import { COLOUR_INSTANCE_EXECUTIONS, COLOUR_INSTANCE_TIME } from "../component/Constants";
import { ChartPlotLine } from "../types/ChartPlotLine";
import dayjs from "dayjs";
import { getMinMAx } from "./utils";
import { resolveUserTimezone } from "./timeRangeUtils";
import Helper from "./Helper";

const COLORS: { [key: string]: string } = {
    'Executions': COLOUR_INSTANCE_EXECUTIONS,
    'Time Spent': COLOUR_INSTANCE_TIME
}

export const chartOptionsGranularity = (series: any[], max: any, options: any = {}, height: number = 200) => {
    // let pseudoInterval = 0
    let interval = 0
    let roundedMinX = 0
    let roundedMaxX = 0

    let minMax: { minX: number | undefined, maxX: number | undefined } = {
        minX: undefined,
        maxX: undefined
    }

    const gapSize = options.columnGapSize ?? 2
    series.forEach((set: any) => {
        set.color = set.color || COLORS[set.name]
        set.gapSize = gapSize
        set.gapUnit = 'relative'
    })

    const maxValue = max || (series[0] ? Math.max(...(series[0]?.data.map((i: any) => i[1]))) : 0)

    if (options?.plotLines) {
        options.plotLines = options.plotLines.map((chartPlotLine: ChartPlotLine) => {
            return {
                ...chartPlotLine,
                value: chartPlotLine.timeValue
            }
        })
    }

    // Calculate total number of legend items
    const totalItems = series.length;

    if (options.timeRangeContext) {
        interval = options.timeRangeContext.timeRange?.interval
        minMax = getMinMAx(options.timeRangeContext)
        const incrementUnit = Helper.getInterval(options.timeRangeContext.timeRange?.from, options.timeRangeContext.timeRange?.to, options.timeRangeContext.timeRange?.interval)
        if (minMax.maxX && minMax.minX) {
            roundedMinX = Math.floor(minMax.minX / incrementUnit) * incrementUnit;
            roundedMaxX = Math.ceil(minMax.maxX / incrementUnit) * incrementUnit;
        }
    }

    const selectPeriod = (time: { start: number, end: number }) => {
        const timeZoneValue = resolveUserTimezone();

        let startTime = timeZoneValue === 'UTC' ? dayjs.utc(time.start) : dayjs(time.start).tz(timeZoneValue);
        let endTime = timeZoneValue === 'UTC' ? dayjs.utc(time.end) : dayjs(time.end).tz(timeZoneValue);
        interval = 0

        if (endTime.diff(startTime, 'minute') < 1) {
            endTime = startTime.add(1, 'minute');
        }

        options.timeRangeContext.setTimeRange({
            from: startTime,
            to: endTime,
            timezone: timeZoneValue,
            interval: interval,
        })

        options.timeRangeContext.applyPeriod(1, startTime, endTime, timeZoneValue);
    }

    const tooltip = {
        ...(options.tooltip || {}),
        ...(options.tooltip ? {
            borderWidth: 0,
            outside: true,
            padding: 0,
            shadow: false,
            shared: true,
            useHTML: true,
            className: 'highcharts-tooltip-single',
            backgroundColor: null,
            borderRadius: 0,
            borderColor: 'none'
        } : {
            shared: true,
            valueDecimals: 0,
            className: 'highcharts-tooltip-single',
            backgroundColor: null,
            borderRadius: 0,
            borderColor: 'none'
        })
    };

    const yAxis = options.yAxis || [{
        labels: {
            enabled: false
        },
        title: {
            text: ''
        },
        min: 0,
        ...(max ? { maxValue } : {})
    }, {
        labels: {
            enabled: false
        },
        min: 0,
        title: {
            text: ''
        },
        opposite: true
    }]

    const hasYAxisLabels = options?.yAxis?.labels?.enabled

    return {
        chart: {
            height: `${height}px`,
            type: 'column',
            zoomType: 'x',
            style: {
                fontFamily: 'system-ui, Arial, sans-serif',
                letterSpacing: 'normal'
            },
            marginLeft: hasYAxisLabels ? 50 : undefined,
            events: {
                selection: function (event: any) {
                    if (event.xAxis) {
                        const start = event.xAxis[0].min;
                        const end = event.xAxis[0].max;
                        selectPeriod({ start, end });
                    }
                    return false; // Don't zoom into the selected area
                },
            }
        },
        title: { text: '' },
        xAxis: [{
            endOnTick: false,
            plotLines: options?.plotLines,
            // plotBands: options?.plotBands,
            // min: minMax.minX ? minMax.minX - pseudoInterval : minMax.minX,
            // min: minMax.minX,
            // max: minMax.maxX,
            min: roundedMinX || minMax.minX,
            max: roundedMaxX || minMax.maxX,
            tickInterval: 'auto',
            type: 'datetime',
            lineColor: '#CCCCCC',
            tickColor: '#CCCCCC',
            labels: {
                distance: 9,
                style: {
                    color: '#666666',
                    cursor: 'default',
                    fontSize: '11px',
                    letterSpacing: '1px'
                }
            },
            crosshair: {
                width: 15,
                color: "rgba(125, 160, 200, 0.15)"
            },
        }],
        time: {
            useUTC: (options.timeRangeContext?.timeRange?.timezone ?? resolveUserTimezone()) === 'UTC'
        },
        yAxis: yAxis,
        plotOptions: {
            animation: false,
            column: {
                stacking: 'normal',
                minPointLength: options.minHeight || 0,
                pointPlacement: options.columnPointPlacement !== undefined ? options.columnPointPlacement : "on"
            },
            area: { stacking: 'normal' },
            series: {
                marker: { enabled: false },
                pointPlacement: options.columnPointPlacement !== undefined ? options.columnPointPlacement : "on"
            }
        },
        exporting: { enabled: false },
        tooltip: tooltip,
        legend: {
            align: 'center',
            backgroundColor: 'transparent',
            enabled: !!options.legend,
            floating: false,
            itemMarginTop: 0,
            itemMarginBottom: 2,
            maxHeight: 70,
            itemStyle: {
                fontSize: '12px',
                fontWeight: 'bold',
                letterSpacing: 'normal',
                fontFamily: 'system-ui, Arial, sans-serif'
            },
            layout: 'horizontal',
            padding: 0,
            x: 0,
            y: 0,
            verticalAlign: 'bottom',
            itemDistance: totalItems > 15 ? 0 : undefined,
            alignColumns: true,
            navigation: {
                activeColor: '#3097d1',
                animation: true,
                arrowSize: 8,
                style: {
                    fontWeight: 'bold',
                    fontSize: '12px'
                }
            }
        },
        series: series,
        credits: options?.credits || { enabled: false },
        accessibility: {
            enabled: false
        }
    }
}